import {
    Organization,
    OrganizationDetail,
    OrganizationLocationRef,
    OrganizationLocationSettings,
    OrganizationMember,
    makeOrganization,
    makeOrganizationLocationSettings,
} from "../_models/Organization";

export class OrganizationMapper {
    static mapAllServerToLocal(serverOrgs: any[] = []): Organization[] {
        return serverOrgs.map(OrganizationMapper.mapServerToLocal);
    }

    static mapServerToLocal(server: any): Organization {
        return makeOrganization({
            id: server.id,
            uid: server.uid,
            name: server.name,
        });
    }

    static mapDetailServerToLocal(server: any): OrganizationDetail {
        const detail: OrganizationDetail = {
            ...OrganizationMapper.mapServerToLocal(server),
            ownerUid: server.owner_uid ?? null,
            // Default to allowed when the backend does not send the field yet
            // (older API): the backend guard is the real enforcement.
            canAddMembers: server.can_add_members ?? true,
            users: (server.users ?? []).map(
                OrganizationMapper.mapMemberServerToLocal,
            ),
            locations: (server.locations ?? []).map(
                OrganizationMapper.mapLocationRefServerToLocal,
            ),
        };

        // Omit the optional property for an old response. Apart from keeping
        // that contract stable, it lets the screen distinguish “API not
        // rolled out yet” from a real template containing all default values.
        const rootSettingFields = [
            "invitation_link_add_to_home",
            "invitation_link_location_check_mode",
            "url_recommendations",
            "early_checkin_enabled",
            "protect_auto_enabled_without_reservations",
        ];
        if (
            server.location_settings !== undefined ||
            rootSettingFields.some((field) =>
                Object.prototype.hasOwnProperty.call(server, field),
            )
        ) {
            detail.locationSettings =
                OrganizationMapper.mapLocationSettingsServerToLocal(
                    // New responses group the defaults. Reading the root too
                    // keeps the client compatible while the API rollout is mixed.
                    server.location_settings ?? server,
                );
        }

        return detail;
    }

    static mapLocationSettingsServerToLocal(
        server: any = {},
    ): OrganizationLocationSettings {
        const disabledLocationCheck =
            server.invitation_link_disable_location_check ?? false;

        return makeOrganizationLocationSettings({
            invitationLinkAddToHome:
                server.invitation_link_add_to_home ?? false,
            invitationLinkAppName: server.invitation_link_app_name ?? "",
            invitationLinkLocationCheckMode:
                server.invitation_link_location_check_mode ??
                (disabledLocationCheck ? "off" : "warn"),
            invitationLinkLocationRadius:
                server.invitation_link_location_radius ?? 200,
            invitationLinkHideHeader:
                server.invitation_link_hide_header ?? false,
            invitationLinkHideFooter:
                server.invitation_link_hide_footer ?? false,
            urlRecommendations: server.url_recommendations ?? "",
            videoName: server.video_name,
            videoUrl: server.video_url,
            externalVideoUrl: server.external_video_url ?? "",
            earlyCheckinEnabled: server.early_checkin_enabled ?? false,
            earlyCheckoutEnabled: server.early_checkout_enabled ?? false,
            protectAutoEnabledWithoutReservations:
                server.protect_auto_enabled_without_reservations ?? false,
            enableProtectGuest: server.enable_protect_guest ?? false,
            securityAlertTime: server.security_alert_time ?? 0,
            embedDomains: Array.isArray(server.embed_domains)
                ? server.embed_domains
                : [],
        });
    }

    static mapMemberServerToLocal(server: any): OrganizationMember {
        return {
            uid: server.uid,
            email: server.email,
            name: server.name,
            // Every member was an admin before roles existed.
            role: server.role ?? "admin",
        };
    }

    static mapLocationRefServerToLocal(server: any): OrganizationLocationRef {
        return {
            uid: server.uid,
            name: server.name,
        };
    }
}
